export interface Stat {
    value: string
    label: string
}

export interface Milestone {
    year: string
    title: string
    text: string
    photo?: string
}

export interface Quote {
    text: string
    source: string
    translated?: boolean
}

export interface SocialLink {
    label: string
    handle: string
    href: string
}

export const profile = {
    name: 'Arian Mavriqi',
    role: 'Wildlife and nature photographer',
    base: 'Kamenica, Kosovo',
    baseLocal: 'Kamenicë',
    coordinates: '42°35′N 21°35′E',
    timeZone: 'Europe/Belgrade',
    since: 2012
}

export const stats: Array<Stat> = [
    {
        value: '210+',
        label: 'Bird species photographed'
    },
    {
        value: '11',
        label: 'Species recorded in Kosovo for the first time through his photographs'
    },
    {
        value: '15',
        label: 'Camera traps keeping watch across Kosovo'
    },
    {
        value: '72h',
        label: 'His longest wait in a single spot'
    }
]

export const milestones: Array<Milestone> = [
    {
        year: '2012',
        title: 'First frames',
        text: 'Macro studies of flowers and insects around Kamenica, made on a phone that Albanians living abroad had given him.',
        photo: 'nursery-web-spider'
    },
    {
        year: '2020',
        title: 'The black wolf',
        text: 'With fellow photographer Besnik Jakupi, he photographs and films a rare black wolf near Kamenica.'
    },
    {
        year: '2022',
        title: 'On tour',
        text: 'An exhibition tour from Scandinavia to Austria and Switzerland, including the Embassy of Kosovo in Bern.',
        photo: 'radoniq-lake'
    },
    {
        year: '2023',
        title: 'Wild Kosovo',
        text: 'His documentary is screened with an exhibition of his photographs at Green Fest in Mitrovica.',
        photo: 'red-deer-in-snow'
    },
    {
        year: '2024',
        title: 'Gjirokastër Castle',
        text: 'An exhibition of Albanian wildlife inside the castle walls, opened with Albania’s Minister of Tourism and Environment.',
        photo: 'dalmatian-pelican'
    },
    {
        year: '2025',
        title: 'The golden oriole',
        text: 'He films golden orioles feeding their young at the nest, and announces his first book, Wild Kosova.'
    }
]

export const quotes: Array<Quote> = [
    {
        text: 'You cannot do anything without patience in this kind of photography.',
        source: 'Interview with Active Albania, 2021'
    },
    {
        text: 'Those who cause problems for them are us.',
        source: 'Katror, 2023',
        translated: true
    },
    {
        text: 'Firstly you should love fauna and understand the importance of the biodiversity.',
        source: 'Interview with Active Albania, 2021'
    },
    {
        text: 'Even if you had a camera, you could be accused of espionage.',
        source: 'On the war years, Gazeta Frymëzimi, 2025',
        translated: true
    },
    {
        text: 'You can even start with a phone.',
        source: 'Advice to beginners, Gazeta Frymëzimi, 2025',
        translated: true
    },
    {
        text: 'With will and desire, everything is possible.',
        source: 'Katror, 2023',
        translated: true
    }
]

export const endorsement: Quote = {
    text: 'An ambassador for the beauty of Albania’s nature, a strong voice for protecting it, and a great storyteller, in his photographs and in his words.',
    source: 'How Mirela Kumbaro, Albania’s Minister of Tourism and Environment, described him when opening his exhibition at Gjirokastër Castle',
    translated: true
}

export const method: Array<{ title: string, text: string }> = [
    {
        title: 'Camouflage',
        text: 'Dressed for the forest floor, so the animal sees landscape instead of a man with a lens.'
    },
    {
        title: 'Stillness',
        text: 'Hours without moving. Sometimes days: his longest wait in one spot lasted three.'
    },
    {
        title: 'Patience',
        text: 'Twelve years, he says, before he could photograph a wolf in the municipality of Kamenica.'
    },
    {
        title: 'Knowing the animal',
        text: 'Above everything else, understanding behaviour: where a bird will land, when a fox will look up.'
    }
]

export const places: Array<{ name: string, country: string }> = [
    {
        name: 'Kamenica',
        country: 'Kosovo'
    },
    {
        name: 'Divjakë–Karavasta',
        country: 'Albania'
    },
    {
        name: 'Narta Lagoon',
        country: 'Albania'
    },
    {
        name: 'Lake Shkodra',
        country: 'Albania'
    },
    {
        name: 'Patok',
        country: 'Albania'
    },
    {
        name: 'Dhërmi',
        country: 'Albania'
    }
]

export const socials: Array<SocialLink> = [
    {
        label: 'Facebook',
        handle: '@arianmavriqiwildlife',
        href: 'https://www.facebook.com/arianmavriqiwildlife/'
    },
    {
        label: 'YouTube',
        handle: '@arianmavriqi',
        href: 'https://www.youtube.com/@arianmavriqi'
    },
    {
        label: 'X',
        handle: '@MavriqiArian',
        href: 'https://x.com/MavriqiArian'
    },
    {
        label: 'TikTok',
        handle: '@arianmavriqi',
        href: 'https://www.tiktok.com/@arianmavriqi'
    },
    {
        label: '500px',
        handle: 'arianmavriqi',
        href: 'https://500px.com/p/arianmavriqi'
    }
]

export const latinNames: Array<string> = [
    'Pandion haliaetus',
    'Ciconia nigra',
    'Upupa epops',
    'Vulpes vulpes',
    'Asio otus',
    'Merops apiaster',
    'Alcedo atthis',
    'Phoenicopterus roseus',
    'Vipera ammodytes',
    'Strix aluco',
    'Cervus elaphus',
    'Pelecanus crispus',
    'Lanius collurio',
    'Sciurus vulgaris'
]
